// 🔥 확장형 가격 테이블 컴포넌트
// [배경] 모델 카드가 확장되었을 때 표시되는 인라인 편집 가능한 가격 테이블
//        각 용량/연결성 조합별 A/B/C/D 등급 가격을 편집
//        태블릿/스마트폰/웨어러블 공통 사용

import React, { memo, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import { DeviceGrade, GradePriceInfo } from '../../../../types/gradeSystem';
import EditablePriceCell, { CellPosition, EditablePriceCellRef } from './EditablePriceCell';
import { getGradesForCategory } from '../../../../constants/grades';

// 📌 가격 행 데이터 인터페이스
export interface PriceRowData {
  priceId: string;
  storage: string;
  connectivity: string;
  prices: GradePriceInfo;
  hasPrice: boolean;
}

// 📌 Props 인터페이스
export interface ExpandablePriceTableProps {
  /** 가격 행 데이터 목록 */
  priceRows: PriceRowData[];
  /** 디바이스 타입 */
  deviceType: 'tablet' | 'smartphone' | 'wearable';
  /** 모델 ID (행 인덱스 계산용) */
  modelId: string;
  /** 전역 행 인덱스 오프셋 (전체 테이블에서의 위치) */
  rowIndexOffset: number;
  /** 셀 값 변경 핸들러 */
  onCellChange: (position: CellPosition, newValue: number) => void;
  /** 셀 네비게이션 핸들러 */
  onNavigate: (from: CellPosition, direction: 'next' | 'prev' | 'up' | 'down') => void;
  /** 셀 레퍼런스 등록 핸들러 */
  registerCellRef: (position: CellPosition, ref: EditablePriceCellRef | null) => void;
  /** 현재 활성화된 셀 */
  activeCell: CellPosition | null;
  /** 활성 셀 설정 핸들러 */
  setActiveCell: (position: CellPosition | null) => void;
  /** 특정 셀이 변경되었는지 확인 */
  isCellChanged: (priceId: string, grade: DeviceGrade) => boolean;
  /** 특정 셀의 현재 값 (변경된 값 포함) */
  getCellValue: (priceId: string, grade: DeviceGrade, originalValue: number) => number;
  /** 저장 완료된 셀 목록 */
  savedCells: Set<string>;
  /** 저장 중 여부 */
  isSaving: boolean;
  /** 저장 중인 priceId Set */
  savingPriceIds?: Set<string>;
  /** 히스토리 보기 클릭 핸들러 */
  onHistoryClick?: (priceId: string, modelName: string) => void;
  /** 구성 삭제 클릭 핸들러 */
  onDeleteClick?: (priceId: string, storage: string, connectivity: string) => void;
  /** 모델명 (히스토리용) */
  modelName: string;
  /** 🔥 변경된 셀 개수 (리렌더링 트리거용) */
  changedCellCount?: number;
  /** 🔥 저장 완료된 셀 개수 (리렌더링 트리거용) */
  savedCellCount?: number;
}

// 🎨 등급별 색상
const GRADE_COLORS: Record<DeviceGrade, string> = {
  A: '#059669',
  B: '#0ea5e9',
  C: '#f59e0b',
  D: '#ef4444',
  E: '#6b7280'
};

/**
 * 🔥 확장형 가격 테이블 컴포넌트
 * 
 * @description
 * 모델 카드 내에서 확장 시 표시되는 인라인 편집 가능한 가격 테이블입니다.
 * - 각 용량/연결성 조합별 A/B/C/D 등급 가격 편집
 * - 엑셀 스타일 키보드 네비게이션 지원
 * - 변경된 셀 시각적 강조
 */
const ExpandablePriceTable: React.FC<ExpandablePriceTableProps> = ({
  priceRows,
  deviceType,
  modelId,
  rowIndexOffset,
  onCellChange,
  onNavigate,
  registerCellRef,
  activeCell,
  setActiveCell,
  isCellChanged,
  getCellValue,
  savedCells,
  isSaving,
  savingPriceIds,
  onHistoryClick,
  onDeleteClick,
  modelName,
  changedCellCount = 0,
  savedCellCount = 0
}) => {
  // 📊 카테고리별 표시 등급
  const displayGrades = getGradesForCategory(deviceType) as DeviceGrade[];

  // 📊 등급 명칭 반환
  const getGradeLabel = (grade: DeviceGrade): string => {
    return `${grade}등급`;
  };

  // 🔥 가격 셀 렌더링
  const renderPriceCell = useCallback((
    priceId: string,
    grade: DeviceGrade,
    originalValue: number,
    rowIndex: number
  ) => {
    const position: CellPosition = {
      priceId,
      grade,
      rowIndex: rowIndexOffset + rowIndex,
      colIndex: ['A', 'B', 'C', 'D', 'E'].indexOf(grade)
    };

    const displayValue = getCellValue(priceId, grade, originalValue);
    const isChanged = isCellChanged(priceId, grade);
    const isSaved = savedCells.has(`${priceId}-${grade}`);
    const isRowSaving = savingPriceIds?.has(priceId) || isSaving;

    return (
      <EditablePriceCell
        key={`${priceId}-${grade}`}
        value={originalValue}
        displayValue={displayValue}
        position={position}
        onChange={onCellChange}
        onNavigate={onNavigate}
        onEditStart={(pos) => setActiveCell(pos)}
        onEditEnd={() => setActiveCell(null)}
        gradeColor={GRADE_COLORS[grade]}
        disabled={isRowSaving}
        forceEdit={activeCell?.priceId === priceId && activeCell?.grade === grade}
        isChanged={isChanged}
        isSaved={isSaved}
        ref={(ref) => registerCellRef(position, ref)}
      />
    );
  }, [
    rowIndexOffset,
    getCellValue,
    isCellChanged,
    savedCells,
    savingPriceIds,
    isSaving,
    onCellChange,
    onNavigate,
    setActiveCell,
    activeCell,
    registerCellRef
  ]);

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc' }}>
            <TableCell sx={{ fontWeight: 700, minWidth: 80, fontSize: '0.75rem' }}>
              용량
            </TableCell>
            {deviceType === 'tablet' && (
              <TableCell sx={{ fontWeight: 700, minWidth: 100, fontSize: '0.75rem' }}>
                연결성
              </TableCell>
            )}
            {displayGrades.map(grade => (
              <TableCell
                key={grade}
                sx={{
                  fontWeight: 700,
                  minWidth: 100,
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  color: GRADE_COLORS[grade]
                }}
              >
                {getGradeLabel(grade)}
              </TableCell>
            ))}
            <TableCell sx={{ fontWeight: 700, width: 80, textAlign: 'center', fontSize: '0.75rem' }}>
              액션
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* 🔍 디버그: 각 행의 priceId 확인 */}
          {(() => {
            if (deviceType === 'wearable') {
              console.log(`🔍 [ExpandablePriceTable] ${modelName} 행 렌더링:`);
              priceRows.forEach((r, i) => console.log(`   ${i}: storage="${r.storage}" priceId="${r.priceId}" prices=`, JSON.stringify(r.prices)));
            }
            return null;
          })()}
          {priceRows.map((row, idx) => {
            const hasAnyPrice = Object.values(row.prices).some(p => p > 0);
            
            return (
              <TableRow
                key={row.priceId}
                sx={{
                  bgcolor: idx % 2 === 0 ? 'white' : '#f8fafc',
                  borderLeft: !hasAnyPrice ? '3px solid #ef4444' : 'none',
                  '&:hover': { bgcolor: '#f0f9ff' }
                }}
              >
                {/* 용량 */}
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {row.storage}
                    </Typography>
                    {!hasAnyPrice && (
                      <Tooltip title="가격 미설정">
                        <WarningIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>

                {/* 연결성 (태블릿만) */}
                {deviceType === 'tablet' && (
                  <TableCell>
                    <Chip
                      label={row.connectivity}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: '0.7rem',
                        height: 20,
                        color: row.connectivity === 'Wi-Fi' ? '#6b7280' : '#0ea5e9'
                      }}
                    />
                  </TableCell>
                )}

                {/* A/B/C/D 등급 가격 셀 */}
                {displayGrades.map(grade => (
                  <TableCell
                    key={grade}
                    sx={{
                      p: 0.5,
                      ...(activeCell?.priceId === row.priceId && activeCell?.grade === grade && {
                        bgcolor: 'rgba(59, 130, 246, 0.1)',
                        outline: '2px solid #3b82f6'
                      })
                    }}
                  >
                    {renderPriceCell(row.priceId, grade, row.prices[grade] || 0, idx)}
                  </TableCell>
                ))}

                {/* 액션 버튼 */}
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    {/* 히스토리 */}
                    {hasAnyPrice && onHistoryClick && (
                      <Tooltip title="변경 히스토리">
                        <IconButton
                          size="small"
                          onClick={() => onHistoryClick(row.priceId, modelName)}
                          color="info"
                        >
                          <HistoryIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* 삭제 */}
                    {onDeleteClick && (
                      <Tooltip title={`${row.storage} ${row.connectivity} 구성 삭제`}>
                        <IconButton
                          size="small"
                          onClick={() => onDeleteClick(row.priceId, row.storage, row.connectivity)}
                          color="error"
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}

          {/* 빈 데이터 */}
          {priceRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={(deviceType === 'tablet' ? 3 : 2) + displayGrades.length + 1} sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  등록된 용량/연결성 구성이 없습니다.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// 🔧 displayName 설정
ExpandablePriceTable.displayName = 'ExpandablePriceTable';

// 🔥 React.memo로 최적화
export default memo(ExpandablePriceTable, (prevProps, nextProps) => {
  // 주요 props 비교
  return (
    prevProps.modelId === nextProps.modelId &&
    prevProps.priceRows.length === nextProps.priceRows.length &&
    prevProps.isSaving === nextProps.isSaving &&
    prevProps.activeCell?.priceId === nextProps.activeCell?.priceId &&
    prevProps.activeCell?.grade === nextProps.activeCell?.grade &&
    // 🔥 변경/저장 셀 개수 비교 (중요! pendingChanges 반영을 위함)
    prevProps.changedCellCount === nextProps.changedCellCount &&
    prevProps.savedCellCount === nextProps.savedCellCount &&
    // 가격 변경 감지 (간단한 비교)
    JSON.stringify(prevProps.priceRows.map(r => r.prices)) ===
    JSON.stringify(nextProps.priceRows.map(r => r.prices))
  );
});
